import { useNavigate } from "react-router-dom";

// ============================================
// TERMS OF SERVICE PAGE
// Publicly accessible (outside SignedIn wrapper)
// Renders the TOS in a clean, readable layout
// ============================================

function IconShield({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
    </svg>
  );
}

function IconArrowLeft({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
    </svg>
  );
}

export default function Terms() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
          >
            <IconArrowLeft className="w-5 h-5 text-slate-500" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center shadow-sm">
              <IconShield className="w-4 h-4 text-white" />
            </div>
            <span className="font-semibold text-slate-800">PeerPrep</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 sm:p-12">
          <h1 className="text-2xl font-bold text-slate-900 mb-1">Terms of Service</h1>
          <p className="text-sm text-slate-500 mb-8">Last Updated: March 6, 2026</p>

          <div className="prose prose-slate prose-sm max-w-none
            prose-headings:text-slate-800 prose-headings:font-semibold
            prose-h2:text-lg prose-h2:mt-10 prose-h2:mb-4 prose-h2:pt-6 prose-h2:border-t prose-h2:border-slate-100
            prose-h3:text-base prose-h3:mt-6 prose-h3:mb-3
            prose-p:text-slate-600 prose-p:leading-relaxed
            prose-a:text-teal-600 prose-a:no-underline hover:prose-a:underline
            prose-strong:text-slate-700
            prose-li:text-slate-600
          ">
            <p>
              By creating an account or using PeerPrep ("the Service"), you ("you," "Provider," or "User") agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, do not create an account or use the Service.
            </p>
            <p>
              PeerPrep is operated by Jamie Harkey ("PeerPrep," "we," "us," or "our").
            </p>

            <h2>1. Description of Service</h2>
            <p>
              PeerPrep is a software platform designed to assist licensed healthcare providers in navigating prior authorization processes and peer-to-peer insurance review calls. The Service provides clinical decision support tools, including but not limited to: prior authorization letter generation, real-time call preparation, rebuttal guidance, clinical guideline references, and insurer intelligence.
            </p>
            <p>
              PeerPrep is a clinical support tool. It does not provide medical advice, does not make clinical decisions, and does not replace the independent professional judgment of a licensed healthcare provider. All clinical decisions remain the sole responsibility of the treating provider.
            </p>

            <h2>2. Eligibility and Account Requirements</h2>

            <h3>2.1 Licensed Provider Requirement</h3>
            <p>
              The Service is available exclusively to licensed healthcare providers, including physicians (MD/DO), nurse practitioners (NP), physician assistants (PA), and other licensed clinicians who are actively involved in ordering, prescribing, or authorizing patient care. By creating an account, you represent and warrant that you are a licensed healthcare provider in good standing with the relevant licensing authority in your jurisdiction.
            </p>

            <h3>2.2 Individual Use Only</h3>
            <p>
              Each PeerPrep account is for the sole, exclusive use of the individual provider who creates it. You may not share your account credentials with any other person, permit any other person to access the Service through your account, or create an account on behalf of another individual. You are responsible for all activity that occurs under your account.
            </p>

            <h3>2.3 Accurate Registration</h3>
            <p>
              You agree to provide accurate, current, and complete information during the registration process, including your name, professional credentials, and contact information. You agree to update this information promptly if it changes. We reserve the right to verify your credentials and to suspend or terminate accounts that contain inaccurate or fraudulent registration information.
            </p>

            <h2>3. Insurer Affiliation and Non-Disclosure</h2>

            <h3>3.1 No Insurer Affiliation</h3>
            <p>By creating an account, you represent and warrant that:</p>
            <p>
              (a) You are not currently employed by, contracted with, or serving as a medical director, medical reviewer, peer reviewer, or consultant for any health insurance company, health plan, managed care organization, utilization review organization, or any entity that adjudicates, reviews, or makes determinations on prior authorization requests or insurance claims (collectively, "Insurance Entities").
            </p>
            <p>
              (b) You are not creating this account on behalf of, at the direction of, or for the benefit of any Insurance Entity.
            </p>
            <p>
              (c) If your employment or affiliation status changes such that you become affiliated with an Insurance Entity in any capacity described in subsection (a), you will immediately notify PeerPrep and discontinue use of the Service.
            </p>

            <h3>3.2 Non-Disclosure Obligation</h3>
            <p>You agree that you will not disclose, share, reproduce, or distribute any of the following to any Insurance Entity or any individual acting on behalf of an Insurance Entity:</p>
            <p>
              (a) The specific rebuttal strategies, argument frameworks, or clinical talking points provided by the Service;
            </p>
            <p>
              (b) The insurer intelligence data, criteria system mappings, or insurer-specific behavioral patterns contained in the Service;
            </p>
            <p>
              (c) The structure, logic, or methodology of the Service's clinical decision support tools;
            </p>
            <p>
              (d) Screenshots, recordings, exports, or reproductions of any content generated by or displayed within the Service, except as necessary for the direct patient care purposes for which the content was generated (e.g., submitting a prior authorization letter to an insurer on behalf of a specific patient).
            </p>
            <p>
              This non-disclosure obligation survives termination of your account and remains in effect indefinitely.
            </p>

            <h3>3.3 Permitted Use of Generated Content</h3>
            <p>
              Nothing in Section 3.2 restricts you from submitting prior authorization letters, appeal letters, or other patient-specific clinical correspondence generated by the Service to an Insurance Entity in the ordinary course of patient care. The non-disclosure obligation applies to the Service's underlying methods, data, and strategies — not to the patient-care documents you generate using the Service.
            </p>

            <h3>3.4 Remedies</h3>
            <p>
              Violation of Section 3.1 or 3.2 constitutes a material breach of these Terms. In the event of such a breach, PeerPrep reserves the right to immediately terminate your account, pursue injunctive relief, and seek damages, including consequential damages arising from the unauthorized disclosure.
            </p>

            <h2>4. Privacy and Data Handling</h2>

            <h3>4.1 De-Identified by Design</h3>
            <p>
              PeerPrep is architected to avoid the collection, transmission, or storage of Protected Health Information ("PHI") as defined under the Health Insurance Portability and Accountability Act ("HIPAA"). Clinical text entered into the Service is processed through a client-side de-identification pipeline before it reaches our servers. PeerPrep does not require, request, or knowingly store patient names, dates of birth, Social Security numbers, medical record numbers, or other direct patient identifiers.
            </p>

            <h3>4.2 Provider's Responsibility</h3>
            <p>
              While PeerPrep employs technical safeguards to de-identify data before transmission, you remain responsible for your own HIPAA compliance obligations as a covered entity or business associate. You acknowledge that no automated de-identification system is perfect, and you agree to exercise reasonable care when entering clinical information into the Service. You should avoid entering patient identifiers to the extent practicable.
            </p>

            <h3>4.3 Data We Collect</h3>
            <p>
              We collect information necessary to operate the Service, including: your registration information (name, credentials, email, practice information), your usage of the Service (features accessed, cases created, sessions), and technical data (device type, browser, IP address). Our collection and use of this information is governed by our <a href="/privacy">Privacy Policy</a>.
            </p>

            <h3>4.4 No Sale of Provider Data</h3>
            <p>
              We do not sell, license, or share your personal information or usage data with Insurance Entities, data brokers, pharmaceutical companies, or any third party for marketing or commercial purposes unrelated to operating the Service.
            </p>

            <h2>5. Acceptable Use</h2>

            <h3>5.1 Permitted Uses</h3>
            <p>
              You may use the Service solely for the purpose of supporting your clinical practice in navigating prior authorization and utilization review processes for your patients.
            </p>

            <h3>5.2 Prohibited Uses</h3>
            <p>You agree not to:</p>
            <p>(a) Use the Service for any purpose other than direct clinical support of your own patients;</p>
            <p>(b) Use the Service to generate fraudulent, fabricated, or materially misleading clinical documentation;</p>
            <p>(c) Reverse-engineer, decompile, disassemble, or attempt to derive the source code, algorithms, or data structures of the Service;</p>
            <p>(d) Use automated tools, bots, scrapers, or similar technologies to access or extract data from the Service;</p>
            <p>(e) Resell, sublicense, or redistribute access to the Service or any content generated by the Service;</p>
            <p>(f) Use the Service in any manner that violates applicable federal, state, or local laws or regulations, including but not limited to laws governing healthcare, insurance, and professional licensure;</p>
            <p>(g) Circumvent, disable, or interfere with any security or access-control features of the Service;</p>
            <p>(h) Use the Service to compile competitive intelligence for the purpose of developing a competing product.</p>

            <h2>6. Subscription and Payment</h2>

            <h3>6.1 Free Tier</h3>
            <p>
              PeerPrep offers limited free access to certain features, including a limited number of prior authorization letter generations per month. Free tier access is subject to change at our discretion.
            </p>

            <h3>6.2 Paid Subscription</h3>
            <p>
              Access to the full feature set requires a paid subscription. Subscription plans, pricing, and billing terms will be presented to you prior to purchase. By subscribing, you authorize us to charge the payment method you provide on a recurring basis at the then-current rate until you cancel.
            </p>

            <h3>6.3 Cancellation</h3>
            <p>
              You may cancel your subscription at any time through your account settings. Cancellation takes effect at the end of the current billing period. No refunds are provided for partial billing periods.
            </p>

            <h3>6.4 Changes to Pricing</h3>
            <p>
              We may change subscription pricing with at least thirty (30) days' notice. Your continued use of the Service after a price change takes effect constitutes acceptance of the new pricing.
            </p>

            <h2>7. Intellectual Property</h2>

            <h3>7.1 Ownership</h3>
            <p>
              The Service, including all software, data, content, design, trademarks, and documentation, is the exclusive property of PeerPrep and is protected by applicable intellectual property laws. These Terms do not grant you any ownership interest in the Service.
            </p>

            <h3>7.2 License to Use</h3>
            <p>
              Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for its intended purpose during the term of your account.
            </p>

            <h3>7.3 Your Content</h3>
            <p>
              You retain ownership of the clinical information you input into the Service and the patient-care documents generated from that information. By using the Service, you grant us a limited license to process your inputs solely for the purpose of providing the Service to you. We do not claim ownership of your clinical inputs or generated outputs.
            </p>

            <h2>8. Disclaimers</h2>

            <h3>8.1 No Medical Advice</h3>
            <p className="uppercase text-xs tracking-wide font-semibold !text-slate-700">
              The Service is a clinical support tool and does not constitute medical advice, a medical opinion, or a standard of care. All clinical decisions remain the sole responsibility of the treating provider. PeerPrep does not guarantee that use of the Service will result in approval of any prior authorization request, appeal, or insurance claim.
            </p>

            <h3>8.2 No Legal Advice</h3>
            <p>
              The Service is not a law firm and does not provide legal advice. Information about insurer policies, regulatory requirements, or appeals processes is provided for informational purposes only and should not be construed as legal guidance.
            </p>

            <h3>8.3 Clinical Guidelines</h3>
            <p>
              Clinical guidelines referenced by the Service are sourced from publicly available professional society publications. PeerPrep does not guarantee the accuracy, completeness, or currency of these references. Providers are responsible for verifying all clinical citations independently.
            </p>

            <h3>8.4 Service Availability</h3>
            <p className="uppercase text-xs tracking-wide font-semibold !text-slate-700">
              The Service is provided "as is" and "as available." We do not warrant that the Service will be uninterrupted, error-free, or free of harmful components. We disclaim all warranties, express or implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>

            <h2>9. Limitation of Liability</h2>
            <p className="uppercase text-xs tracking-wide font-semibold !text-slate-700">
              To the maximum extent permitted by applicable law, PeerPrep and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of revenue, loss of patients, denial of insurance claims, or professional liability claims, arising out of or related to your use of the Service.
            </p>
            <p className="uppercase text-xs tracking-wide font-semibold !text-slate-700">
              Our total aggregate liability arising out of or related to these Terms shall not exceed the amount you have paid to PeerPrep in the twelve (12) months preceding the event giving rise to the claim, or one hundred dollars ($100), whichever is greater.
            </p>

            <h2>10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless PeerPrep and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or related to: (a) your use of the Service; (b) your violation of these Terms; (c) your violation of any applicable law or regulation; (d) any clinical decision you make in connection with patient care; or (e) your breach of the insurer affiliation or non-disclosure provisions in Section 3.
            </p>

            <h2>11. Term and Termination</h2>

            <h3>11.1 Term</h3>
            <p>These Terms are effective upon your creation of an account and remain in effect until your account is terminated.</p>

            <h3>11.2 Termination by You</h3>
            <p>You may terminate your account at any time by contacting us or using the account deletion feature in the Service.</p>

            <h3>11.3 Termination by Us</h3>
            <p>
              We may suspend or terminate your account at any time, with or without cause, including but not limited to: violation of these Terms, suspected insurer affiliation, inactivity, or discontinuation of the Service. We will make reasonable efforts to provide notice before termination, except where immediate termination is warranted by a material breach.
            </p>

            <h3>11.4 Effect of Termination</h3>
            <p>
              Upon termination, your right to access the Service ceases immediately. Sections 3 (Insurer Affiliation and Non-Disclosure), 7.1 (Ownership), 8 (Disclaimers), 9 (Limitation of Liability), 10 (Indemnification), and 12 (General) survive termination.
            </p>

            <h2>12. General Provisions</h2>

            <h3>12.1 Governing Law</h3>
            <p>These Terms shall be governed by and construed in accordance with the laws of the State of North Carolina, without regard to its conflict of law principles.</p>

            <h3>12.2 Dispute Resolution</h3>
            <p>
              Any dispute arising out of or relating to these Terms or the Service shall be resolved by binding arbitration administered by the American Arbitration Association under its Commercial Arbitration Rules. Each party shall bear its own costs. You agree that any dispute resolution proceedings will be conducted on an individual basis and not as a class action.
            </p>

            <h3>12.3 Modifications</h3>
            <p>
              We may modify these Terms at any time by posting the revised version to the Service. Material changes will be communicated to you via email or in-app notification at least thirty (30) days before they take effect. Your continued use of the Service after the effective date of revised Terms constitutes acceptance.
            </p>

            <h3>12.4 Severability</h3>
            <p>If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.</p>

            <h3>12.5 Entire Agreement</h3>
            <p>These Terms, together with the Privacy Policy, constitute the entire agreement between you and PeerPrep with respect to the Service and supersede all prior agreements.</p>

            <h3>12.6 No Waiver</h3>
            <p>Our failure to enforce any provision of these Terms shall not constitute a waiver of that provision or any other provision.</p>

            <h3>12.7 Assignment</h3>
            <p>You may not assign or transfer your rights or obligations under these Terms without our prior written consent. We may assign these Terms without restriction.</p>

            <h2>13. Beta Program</h2>
            <p>The Service is currently in a beta testing phase. During the beta period:</p>
            <p>(a) Features may be added, modified, or removed without notice;</p>
            <p>(b) The Service may contain bugs, errors, or incomplete features;</p>
            <p>(c) We may collect additional feedback and usage data to improve the Service;</p>
            <p>(d) Access to the beta is provided at no charge or at a reduced rate and may be revoked at any time;</p>
            <p className="uppercase text-xs tracking-wide font-semibold !text-slate-700">
              (e) The beta service is provided with no warranty of any kind. Your use of beta features is at your own risk.
            </p>
            <p>
              Your participation in the beta does not entitle you to any future access, pricing, or features upon general availability.
            </p>

            <h2>14. Contact</h2>
            <p>If you have questions about these Terms, please contact us at:</p>
            <p>
              <strong>Jamie Harkey</strong><br />
              Email: <a href="mailto:jamieharkeyconsulting@gmail.com">jamieharkeyconsulting@gmail.com</a>
            </p>
          </div>

          {/* Agreement footer */}
          <div className="mt-10 pt-6 border-t border-slate-100 text-center">
            <p className="text-xs text-slate-400 italic">
              By checking "I agree to the Terms of Service" during account creation, you acknowledge that you have read, understood, and agree to be bound by these Terms.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom padding */}
      <div className="h-16" />
    </div>
  );
}